// Thin fetch client for the Functions API. Same-origin, JSON in/out.
import type { Insurer, Estimate, ShopsResponse } from "../shared/types";

// Error with the HTTP status attached, so callers can tell "gone" (404)
// apart from transient failures worth retrying.
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export interface SessionState {
  id: string;
  insurerId: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleYear: number | null;
  collisionType: string | null;
  impactPoints: string[];
  symptoms: string[];
  locationZip: string | null;
  locationLat: number | null;
  locationLng: number | null;
  contact: { name?: string | null; email?: string | null; phone?: string | null } | null;
  status: string;
}

export interface PhotoRef {
  id: string;
  label: string | null;
  created_at?: string;
  createdAt?: string;
}

export const listInsurers = () =>
  req<{ insurers: Insurer[] }>("/api/insurers").then((r) => r.insurers);

export const createSession = (consentVersion: string) =>
  req<{ id: string }>("/api/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ consent: true, consentVersion }),
  });

export const getSession = (id: string) =>
  req<{
    session: SessionState;
    photos: { id: string; label: string | null; created_at: string }[];
  }>(`/api/session/${id}`);

export const patchSession = (id: string, patch: Record<string, unknown>) =>
  req<{ session: SessionState }>(`/api/session/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  }).then((r) => r.session);

export async function uploadPhoto(sessionId: string, file: File, label?: string) {
  const form = new FormData();
  form.append("file", file);
  if (label) form.append("label", label);
  return req<PhotoRef>(`/api/session/${sessionId}/photos`, {
    method: "POST",
    body: form,
  });
}

export const deletePhoto = (sessionId: string, photoId: string) =>
  req<{ ok: true }>(`/api/session/${sessionId}/photos/${photoId}`, {
    method: "DELETE",
  });

export const photoUrl = (sessionId: string, photoId: string) =>
  `/api/session/${sessionId}/photos/${photoId}`;

export const generateEstimate = (sessionId: string) =>
  req<{ reportId: string; expiresAt: string; estimate: Estimate }>(
    `/api/session/${sessionId}/estimate`,
    { method: "POST" },
  );

export interface ShopQuery {
  insurerId?: string | null;
  lat?: number | null;
  lng?: number | null;
  zip?: string | null;
  region?: string;
}

export const getShops = (q: ShopQuery) => {
  const params = new URLSearchParams();
  if (q.insurerId) params.set("insurerId", q.insurerId);
  if (q.lat != null && q.lng != null) {
    params.set("lat", String(q.lat));
    params.set("lng", String(q.lng));
  } else if (q.zip) {
    params.set("zip", q.zip);
  }
  if (q.region) params.set("region", q.region);
  return req<ShopsResponse>(`/api/shops?${params.toString()}`);
};

export const getReport = (reportId: string) =>
  req<{ reportId: string; createdAt: string; estimate: Estimate }>(
    `/api/report/${reportId}`,
  );
